import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const MediaLightbox = ({ media, initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const currentItem = media[currentIndex];

  const goNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % media.length);
  };

  const goPrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') goNext(e);
      if (e.key === 'ArrowLeft') goPrev(e);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-6xl w-full bg-black/90 rounded-2xl overflow-hidden flex flex-col max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ─── Header: Close + title + counter ─── */}
        <div className="flex items-center justify-between p-3 sm:p-4 bg-black/80 border-b border-white/10">
          <span className="text-white font-medium text-sm sm:text-base truncate max-w-[60%]">
            {currentItem.title}
          </span>
          <div className="flex items-center gap-3">
            <span className="text-gray-400 text-xs sm:text-sm whitespace-nowrap">
              {currentIndex + 1} / {media.length}
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition border border-white/20"
              aria-label="Close"
            >
              <X size={18} className="sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* ─── Media content ─── */}
        <div className="relative flex-1 bg-black flex items-center justify-center min-h-[300px]">
          {currentItem.type === 'image' ? (
            <img
              src={currentItem.src}
              alt={currentItem.title}
              className="max-w-full max-h-[70vh] object-contain"
            />
          ) : (
            <div className="relative w-full aspect-video">
              <iframe
                src={currentItem.embed}
                title={currentItem.title}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>

        {/* ─── Footer: Navigation arrows + counter ─── */}
        {media.length > 1 && (
          <div className="flex items-center justify-between p-3 sm:p-4 bg-black/80 border-t border-white/10">
            <button
              onClick={goPrev}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition border border-white/20"
              aria-label="Previous"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-gray-400 text-xs sm:text-sm">
              {currentIndex + 1} / {media.length}
            </span>
            <button
              onClick={goNext}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition border border-white/20"
              aria-label="Next"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MediaLightbox;